import { useEffect, useRef, useCallback } from 'react';
import dataManager from '../utils/DataManager';

const IST_OFFSET   = 330;
const WINDOW_START = 11 * 60;
const WINDOW_END   = 17 * 60;
const MIN_INTERVAL = 45 * 60 * 1000;
const MAX_INTERVAL = 120 * 60 * 1000;
const STORAGE_KEY  = (name) => `kairali_followup_next_${name}`;
const HASH_KEY     = (name) => `kairali_followup_hash_${name}`;

const MONTH_MAP = { jan:0,feb:1,mar:2,apr:3,may:4,jun:5,jul:6,aug:7,sep:8,oct:9,nov:10,dec:11 };

// Robust date parser — handles YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY, DD-Mon-YYYY
function parseDate(str) {
  if (!str) return null;
  const s = str.trim();
  // ISO: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.substring(0, 10));
    return isNaN(d) ? null : d;
  }
  const parts = s.split(/[-/]/);
  if (parts.length < 3) return null;
  const p0 = parts[0], p1 = parts[1], p2 = parts[2].substring(0, 4);
  // DD-Mon-YYYY
  const mon = MONTH_MAP[p1.toLowerCase().substring(0, 3)];
  if (mon !== undefined) {
    const d = new Date(parseInt(p2, 10), mon, parseInt(p0, 10));
    return isNaN(d) ? null : d;
  }
  // DD/MM/YYYY or DD-MM-YYYY
  const day = parseInt(p0, 10), month = parseInt(p1, 10) - 1, year = parseInt(p2, 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
  return new Date(year, month, day);
}

function todayMidnight() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function getISTMinutes() {
  const now = new Date();
  const ist = new Date(now.getTime() + (now.getTimezoneOffset() + IST_OFFSET) * 60000);
  return ist.getHours() * 60 + ist.getMinutes();
}

function isInWindow() {
  const m = getISTMinutes();
  return m >= WINDOW_START && m < WINDOW_END;
}

function msUntilNext(storedNext) {
  const now = Date.now();
  if (storedNext && storedNext > now) return storedNext - now;
  const istNow = getISTMinutes();
  if (istNow >= WINDOW_END) return null;
  const remainingMs = (WINDOW_END - Math.max(istNow, WINDOW_START)) * 60000;
  if (remainingMs < MIN_INTERVAL) return null;
  return MIN_INTERVAL + Math.random() * Math.min(MAX_INTERVAL - MIN_INTERVAL, remainingMs - MIN_INTERVAL);
}

function playChime() {
  try {
    const ctx  = new (window.AudioContext || window.webkitAudioContext)();
    const play = (freq, startAt, duration, vol = 0.25) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, startAt);
      gain.gain.setValueAtTime(vol, startAt);
      gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);
      osc.start(startAt); osc.stop(startAt + duration);
    };
    const t = ctx.currentTime;
    play(660, t, 0.4); play(880, t + 0.18, 0.5); play(1100, t + 0.36, 0.6, 0.18);
  } catch {}
}

function simpleHash(obj) {
  const s = JSON.stringify(obj);
  let h = 0;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h + s.charCodeAt(i)) | 0; }
  return String(h);
}

function buildMetrics(delTasks, htTasks, fmsTasks, uname) {
  const today = todayMidnight();

  // Delegation
  const tasks     = Array.isArray(delTasks) ? delTasks : [];
  const getStatus = (t) => (t.delegation_status || '').toLowerCase();
  const getScore  = (t) => {
    const key = Object.keys(t).find(k => k.includes('80') || k.includes('20'));
    return key ? parseFloat(t[key] || 0) : 0;
  };
  const isOverdue = (t) => {
    if (getStatus(t).includes('completed')) return false;
    const d = parseDate(t.final_date || t.first_date || '');
    return d ? d < today : false;
  };
  const isDueToday = (t) => {
    if (getStatus(t).includes('completed')) return false;
    const d = parseDate(t.final_date || '');
    return d ? d.getTime() === today.getTime() : false;
  };

  const total     = tasks.length;
  const completed = tasks.filter(t => getStatus(t).includes('completed')).length;
  const pending   = tasks.filter(t => getStatus(t).includes('pending')).length;
  // grown = tasks created in the last 7 days
  const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const grown = tasks.filter(t => {
    const d = parseDate(t.task_created_date || t.first_date || '');
    return d && d >= sevenDaysAgo;
  }).length;
  const overdueTasks = tasks.filter(isOverdue);
  const due_today    = tasks.filter(isDueToday).length;
  const overdue      = overdueTasks.length;
  const top_overdue  = overdueTasks
    .sort((a, b) => getScore(b) - getScore(a))
    .slice(0, 3).map(t => ({ id: t.did, task: (t.task || '').substring(0, 80) })).filter(t => t.task);

  // HT
  const ht = Array.isArray(htTasks) ? htTasks : [];
  const ht_reply_pending   = ht.filter(t => t.issueDelegatedTo === uname && t.replyPlanned?.trim() && !t.replyActual?.trim()).length;
  const ht_resolve_pending = ht.filter(t => t.name === uname && t.replyActual?.trim() && !t.resolveActual?.trim()).length;

  // FMS + Checklist
  const fms = Array.isArray(fmsTasks) ? fmsTasks : [];
  // delay field is HH:MM:SS — parse total hours
  const delayHours = (raw) => {
    if (!raw) return 0;
    const s = String(raw).trim();
    if (s.includes(':')) {
      const p = s.split(':');
      return (parseFloat(p[0]) || 0) + (parseFloat(p[1]) || 0) / 60;
    }
    return parseFloat(s) || 0;
  };
  const fmsDelayed = fms.filter(t => {
    const ft = (t.fms || '').toLowerCase();
    return (t.doer || '') === uname && !ft.includes('checklist') && !ft.includes('helpticket') && delayHours(t.delay) > 0;
  }).length;
  const checklistOverdue = fms.filter(t =>
    (t.fms || '').toLowerCase().includes('checklist task') &&
    ((t.doer || '') === uname || (t.pcdeo || '') === uname) &&
    delayHours(t.delay) > 0
  ).length;

  if (total === 0 && ht_reply_pending === 0 && ht_resolve_pending === 0) return null;

  return {
    total, completed, pending, overdue, due_today, grown,
    progress_ratio: total > 0 ? completed / total : 0,
    top_overdue,
    ht_reply_pending, ht_resolve_pending,
    fms_delayed: fmsDelayed,
    checklist_overdue: checklistOverdue,
  };
}

export default function FollowUpAgent({ currentUser }) {
  const timerRef = useRef(null);

  const fire = useCallback(async () => {
    if (!isInWindow()) return;

    const uname    = currentUser.name;
    const delTasks = dataManager.getDataWithFallback('delegation', uname);
    const htTasks  = dataManager.getDataWithFallback('ht', uname);
    const fmsTasks = dataManager.getDataWithFallback('fms', uname);
    const metrics  = buildMetrics(delTasks, htTasks, fmsTasks, uname);
    if (!metrics) {
      // No tasks yet (data may still be loading) — reschedule and try again
      const nextAt = Date.now() + MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
      localStorage.setItem(STORAGE_KEY(uname), String(nextAt));
      timerRef.current = setTimeout(fire, nextAt - Date.now());
      return;
    }

    // Skip if nothing changed since last ping
    const currentHash = simpleHash({ p: metrics.pending, o: metrics.overdue, hr: metrics.ht_reply_pending, hv: metrics.ht_resolve_pending });
    const lastHash = localStorage.getItem(HASH_KEY(uname));
    if (currentHash === lastHash) {
      // Nothing changed — reschedule silently
      const nextAt = Date.now() + MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
      localStorage.setItem(STORAGE_KEY(uname), String(nextAt));
      timerRef.current = setTimeout(fire, nextAt - Date.now());
      return;
    }

    try {
      const res = await fetch('/api/ai-followup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee: currentUser, metrics }),
      });
      if (!res.ok) {
        console.warn('FollowUpAgent: ai-followup returned', res.status);
        return;
      }
      const data = await res.json();
      if (!data.message) return;

      localStorage.setItem(HASH_KEY(uname), currentHash);
      playChime();
      window.dispatchEvent(new CustomEvent('ai-proactive-message', { detail: { message: data.message, metrics } }));

      const nextAt = Date.now() + MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
      localStorage.setItem(STORAGE_KEY(uname), String(nextAt));
      timerRef.current = setTimeout(fire, nextAt - Date.now());
    } catch (err) {
      console.warn('FollowUpAgent: fetch failed, will retry next cycle', err.message);
      const nextAt = Date.now() + MIN_INTERVAL + Math.random() * (MAX_INTERVAL - MIN_INTERVAL);
      localStorage.setItem(STORAGE_KEY(uname), String(nextAt));
      timerRef.current = setTimeout(fire, nextAt - Date.now());
    }
  }, [currentUser]);

  useEffect(() => {
    const stored = parseInt(localStorage.getItem(STORAGE_KEY(currentUser.name)) || '0', 10);
    const delay  = msUntilNext(stored || null);
    if (delay === null) return;
    timerRef.current = setTimeout(fire, delay);
    return () => clearTimeout(timerRef.current);
  }, [currentUser.name, fire]);

  return null;
}
